import { CompanyInfo, Config } from '@websky/core/src/context';
import { CardType } from '@websky/core/src/PromoLoader/types';
import { getLocale } from '@websky/core/src/utils';
import i18next from 'i18next';
import { OauthServiceType } from '@websky/graphql';

const isProd = process.env.NODE_ENV === 'production';
const currentLocale = getLocale() as string;

export const companyInfo: CompanyInfo = {
	multiInsurance: true,
	specialDocumentForInsurance: false,
	facebook: '',
	instagram: '',
	vk: 'https://vk.com/izhavia',
	twitter: '',
	baggage: `https://izhavia.su/passazhiram/sistema-tarifov?lang=${currentLocale}`,
	meal: `https://izhavia.su/onlayn-servisy/platnoe-pitanie?lang=${currentLocale}`,
	seat: '',
	legal: '',
	privacyPolicy: `https://izhavia.su/o-kompanii/privacypolicy`,
	webskySearchOrder: `https://booking.izhavia.su/websky/?lang=${currentLocale}&search#/search-order`,
	insurance: '',
	loyaltyProgramRegistrationUrl: `https://izhavia.su/ffp/vhod-v-sistemu?lang=${currentLocale}`,
	loyaltyProgramAccountUrl: `https://izhavia.su/ffp/vhod-v-sistemu?lang=${currentLocale}`,
	loyaltyProgramName: '«ТАУ»',
	iataCode: 'I8',
	exarePrivacyPolicyURL: '',
	exareRulesURL: '',
	subsidyFaresConditionURL: '',
	baggageAnimalsURL: '',
	loyalty: {
		links: [
			{
				title: 'About',
				url: `https://izhavia.su/ffp`
			},

			{
				title: 'Earn miles',
				url: `https://izhavia.su/ffp/kopit-bonusy`
			},

			{
				title: 'Use Miles',
				url: `https://izhavia.su/ffp/tratit-bonusy`
			},

			{
				title: 'FAQ',
				url: `https://www.izhavia.su/passazhiram/faq?lang=${currentLocale}`
			}
		]
	}
};

export const i18n: i18next.Resource = {
	ru: {
		LoginPage: {
			'Email / Phone / Card number': 'Эл. почта'
		},
		Title: {
			companyName: 'Ижавиа'
		},
		Warnings: {
			[`Listed below meals serving and ingredients are guaranteed for flights, that depart from Sheremetyevo airport. For other flights meals can differ from listed above. At the same time you can be sure, that new ingredients don't differ in quality.`]: 'Представленный способ сервировки и состав блюд гарантирован для рейсов, вылетающих из аэропорта Шереметьево и Пулково, при вылете из других аэропортов питание может отличаться от представленного. При этом вы можете быть уверены, что новые продукты в составе меню не отличаются от изначальных по характеристикам.'
		},
		Consents: {
			agreement_generalRules: `<span><a href='https://www.izhavia.su/passazhiram/dogovor-vozdushnoy-perevozki-passazhira' target='_blank'>правилами покупки</a></span>`
		},
		Loyalty: {
			'Earn miles': 'Копить бонусы',
			'Use Miles': 'Потратить бонусы',
			'Start saving now': 'Начни экономить сейчас!\nЗарабатывайте бонусы и тратьте их на ближайших рейсах.',
			miles: 'бонусов',
			'miles left for': 'бонусов осталось до',
			'Left {{miles}} for {{level}} status':
				'Осталось {{miles}} <span>{{milesLabel}}<div>Влияют только на переход до следующего уровня</div></span> до уровня {{level}}'
		},
		Checkout: {
			[companyInfo.loyaltyProgramName]: 'ТАУ — Программа лояльности'
		},
		Passenger: {
			'Don\'t have a card yet?': 'Не участвуете в программе лояльности «ТАУ»?'
		},
		Passengers: {
			'2-11 years': '2-12 лет',
			'Children from two to eleven years old': 'Дети от двух до двенадцати лет'
		},
		SeatMap: {
			SeatMap_Standard: ''
		},
		PriceInMiles: {
			miles_1: 'бонус',
			miles_2: 'бонусов',
			miles_3: 'бонуса',
			status_miles_1: 'статусный бонус',
			status_miles_2: 'статусных бонусов',
			status_miles_3: 'статусных бонуса'
		},
		Account: {
			'Status miles': 'Статусные бонусы'
		},
		FareLock: {
			'Fixation the cost of the air ticket': 'Заморозка цены',
			'No need to worry about the price increase or about the fact that all tickets will be sold out, now you can fix the ticket price for {{hours}} {{plural}}':
				'Зафиксируйте стоимость пока тариф есть в наличии. Завершите покупку в течение {{hours}} {{plural}} и купите по наиболее выгодной для вас цене.',
			hours_0: 'часа',
			hours_1: 'часов',
			hours_2: 'часов'
		}
	},
	en: {
		LoginPage: {
			'Email / Phone / Card number': 'Email / Card number'
		},
		Title: {
			companyName: 'Izhavia'
		},
		Warnings: {
			[`Listed below meals serving and ingredients are guaranteed for flights, that depart from Sheremetyevo airport. For other flights meals can differ from listed above. At the same time you can be sure, that new ingredients don't differ in quality.`]: `Listed below meals serving and ingredients are guaranteed for flights, that depart from Sheremetyevo airport. For other flights meals can differ from listed above. At the same time you can be sure, that new ingredients don't differ in quality.`
		},
		Consents: {
			agreement_generalRules: `<span><a href='https://www.izhavia.su/passazhiram/dogovor-vozdushnoy-perevozki-passazhira' target='_blank'>terms of purchase</a></span>`
		},
		Loyalty: {
			'Earn miles': 'Earn bonuses',
			'Use Miles': 'Use bonuses',
			'Start saving now': 'Start saving now!\nEarn bonuses and spend them on the next flights.',
			miles: 'bonuses',
			'miles left for': 'bonuses left for',
			'Left {{miles}} for {{level}} status':
				'Left {{miles}} <span>{{milesLabel}}<div>Only affect the transition to the next level</div></span> for {{level}} level'
		},
		Checkout: {
			[companyInfo.loyaltyProgramName]: 'TAU — Loyalty program'
		},
		Passenger: {
			'Don\'t have a card yet?': `Don't have "TAU" card yet?`
		},
		Passengers: {
			'2-11 years': '2-12 years',
			'Children from two to eleven years old': 'Children from two to twelve years old'
		},
		SeatMap: {
			SeatMap_Standard: ''
		},
		PriceInMiles: {
			miles_1: 'bonus',
			miles_2: 'bonuses',
			miles_3: 'bonuses',
			status_miles_1: 'status bonus',
			status_miles_2: 'status bonuses',
			status_miles_3: 'status bonuses'
		},
		Account: {
			'Status miles': 'Status bonuses'
		},
		FareLock: {
			'Fixation the cost of the air ticket': 'Price freeze',
			'No need to worry about the price increase or about the fact that all tickets will be sold out, now you can fix the ticket price for {{hours}} {{plural}}':
				'Lock in the price while the plan is available. Complete your purchase within {{hours}} {{plural}} and buy at the best price for you.',
			only: 'Only'
		}
	}
};

export const config: Config = {
	global: {
		socialAuthCallbackUrl: 'https://izhavia.su/social-auth',
		showIsActualizationResultOk: false,
		companyInfo,
		findOrderURL: '',
		maxBaggagePerPassenger: 4,
		allowPromoCodes: false,
		i18n,
		yandexMetrikaId: '98799493',
		authMethods: {
			phone: false,
			ffp: true,
			email: true
		},
		socialAuthMethods: [
			{ service: OauthServiceType.Vkontakte, isAvailable: true },
			{
				service: OauthServiceType.Google,
				isAvailable: false
			}
		],
		seatMap: {
			splitByCabin: true
		},
		insurance: {
			upsaleStrategy: [null]
		},
		reserveEngines: {
			siteUrl: ''
		}
	},
	Engine: {
		searchFormURL: isProd ? './' : null,
		calendarDaysCount: 7,
		mealWarning: '',
		promoLoaders: [
			{
				type: CardType.City,
				image: 'https://cdn.websky.aero/content/frontend/images/I8-LoadingBanners/city_bkl.jpg'
			},
			{
				type: CardType.City,
				image: 'https://cdn.websky.aero/content/frontend/images/I8-LoadingBanners/city_klgd.jpg'
			},
			{
				type: CardType.City,
				image: 'https://cdn.websky.aero/content/frontend/images/I8-LoadingBanners/city_msk.jpg'
			},
			{
				type: CardType.City,
				image: 'https://cdn.websky.aero/content/frontend/images/I8-LoadingBanners/city_sch.jpg'
			},
			{
				type: CardType.City,
				image: 'https://cdn.websky.aero/content/frontend/images/I8-LoadingBanners/city_spb.jpg'
			}
		]
	},
	Checkin: {
		CheckinConditions: ['Check-in conditions', 'OpenCloseCheckinCondition', 'BaggageCondition']
	},
	SearchForm: {
		payByMiles: true,
		showPriceGraph: true,
		enableComplexRoute: false,
		showPricesInDatepicker: true
	},
	FlightStatus: {
		playMarketURL: '',
		appStoreURL: ''
	}
};
